import React from 'react';
import {
  Box,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { PartnerCategory } from '../../../../common/enums';
import OptimusButton from '../../../../common/library/Button/Button';
import { useAppSelector } from '../../../../store/hooks';
import { selectCategories } from '../../../../store/slices/dashboardSlice';

interface PartnerCategoryFilterProps {
  selectedCategories: PartnerCategory[];
  onCategoriesChange: (categories: PartnerCategory[]) => void;
  availableCategories?: PartnerCategory[];
}

export function PartnerCategoryFilter({
  selectedCategories,
  onCategoriesChange,
  availableCategories,
}: PartnerCategoryFilterProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const categories = useAppSelector(selectCategories);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const categoriesToDisplay = React.useMemo(() => {
    if (!availableCategories?.length) {
      return categories?.map((cat) => cat.key) || [];
    }
    return availableCategories;
  }, [availableCategories, categories]);

  const getCategoryLabel = (category: PartnerCategory): string => {
    const categoryData = categories?.find((cat) => cat.key === category);
    return categoryData?.displayName || category;
  };

  const isAllCategories = selectedCategories.length === 0;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const setAllCategories = () => {
    onCategoriesChange([]);
  };

  const toggleCategory = (category: PartnerCategory) => {
    // If currently "All categories" (no filter), start a filtered selection
    const current = isAllCategories ? [] : selectedCategories;

    const exists = current.includes(category);
    const next = exists ?
      current.filter((c) => c !== category) :
      [...current, category];

    // If user removes everything, go back to "All categories" (no filter)
    onCategoriesChange(next.length === 0 ? [] : next);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <OptimusButton
          width={theme.spacing(9.75)}
          size="m"
          customVariant="secondaryGray"
          leadingIcon={<FilterAltOutlinedIcon />}
          trailingIcon={
            open ? <KeyboardArrowDownOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />
          }
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
          buttonText={
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600,
            }}>
              {t('button.filter')}
              {selectedCategories.length > 0 && (
                <CheckCircleOutlinedIcon
                  sx={{
                    color: theme.palette.brand.primary,
                    borderRadius: '50%',
                    fontSize: 'small',
                  }}
                />
              )}
            </Box>
          }
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{ 'aria-labelledby': 'category-filter-button' }}
        PaperProps={{
          sx: {
            mt: theme.spacing(0.5),
            minWidth: theme.spacing(31.25),
            maxHeight: theme.spacing(52.5),
            border: '1px solid rgb(226, 226, 228)',
            borderRadius: theme.spacing(1),
            boxShadow: 'rgba(10, 13, 18, 0.05) 0px 1.25px 2.5px 0px',
          },
        }}
      >
        {/* All categories = exclusive option */}
        <MenuItem onClick={setAllCategories} dense>
          <ListItemText
            primary={t('button.allCategories')}
            primaryTypographyProps={{
              sx: {
                fontSize: theme.spacing(1.75),
                fontWeight: 400,
              },
            }}
          />
          {isAllCategories && (
            <CheckIcon sx={{ width: theme.spacing(1.5), height: theme.spacing(1.5) }} />
          )}
        </MenuItem>

        {categoriesToDisplay.map((category) => {
          const checked = !isAllCategories && selectedCategories.includes(category);

          return (
            <MenuItem
              key={category}
              onClick={() => toggleCategory(category)}
              dense
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              <ListItemText
                primary={getCategoryLabel(category)}
                primaryTypographyProps={{
                  sx: {
                    fontSize: theme.spacing(1.75),
                    fontWeight: 400,
                  },
                }}
              />
              {checked && (
                <CheckIcon sx={{ width: theme.spacing(1.5), height: theme.spacing(1.5) }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
